import React from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Container,
  ThemeProvider,
  createTheme,
  useMediaQuery,
} from "@mui/material";
import Logo from "../Images/ERP Logo.png";

// Create a custom theme to match the payslip look with improved typography
const theme = createTheme({
  palette: {
    primary: {
      main: "#1976d2",
    },
    secondary: {
      main: "#f5f5f5",
    },
    grey: {
      main: "#757575",
    },
  },
  typography: {
    fontFamily: "'Roboto', 'Arial', sans-serif",
    h6: {
      fontWeight: 600,
      fontSize: "1rem",
      lineHeight: 1.4,
    },
    body1: {
      fontSize: "0.95rem",
    },
    body2: {
      fontSize: "0.85rem",
    },
    caption: {
      fontSize: "0.75rem",
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: "14px 24px",
          fontSize: "0.875rem",
        },
      },
    },
  },
});

// Function to convert number to words
const convertToWords = (amount) => {
  // Remove "Rs." and commas, then convert to number
  const numericAmount = parseFloat(amount.replace("Rs.", "").replace(/,/g, ""));

  const units = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
  ];
  const teens = [
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const tens = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];

  const convertLessThanOneThousand = (num) => {
    if (num === 0) return "";

    let result = "";

    if (num < 10) result = units[num];
    else if (num < 20) result = teens[num - 10];
    else if (num < 100) {
      result = tens[Math.floor(num / 10)];
      if (num % 10 > 0) result += "-" + units[num % 10];
    } else {
      result = units[Math.floor(num / 100)] + " Hundred";
      if (num % 100 > 0) result += " " + convertLessThanOneThousand(num % 100);
    }

    return result;
  };

  if (numericAmount === 0) return "Zero";

  let result = "";
  let num = Math.floor(numericAmount);
  let paisa = Math.round((numericAmount - num) * 100);

  if (num >= 100000) {
    result += convertLessThanOneThousand(Math.floor(num / 100000)) + " Lakh ";
    num %= 100000;
  }

  if (num >= 1000) {
    result += convertLessThanOneThousand(Math.floor(num / 1000)) + " Thousand ";
    num %= 1000;
  }

  if (num > 0) {
    result += convertLessThanOneThousand(num);
  }

  if (paisa > 0) {
    result += " and " + convertLessThanOneThousand(paisa) + " Paisa";
  }

  return "Indian Rupee " + result.trim() + " Only";
};

const PayslipComponent = ({ payslipData }) => {
  const amountInWords = convertToWords(payslipData.netPayable);

  const getCurrentMonthYear = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = date.toLocaleString("en-US", { month: "long" }).toUpperCase();
    return `${year} ${month}`;
  };

  // Use media query for responsive design
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="lg">
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: 3,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          }}
        >
          {/* Header Section */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between", // equal gap between the 3 sections
              px: 2,
              py: 2,
              mb: 3,
              borderBottom: "2px solid #9e9e9e",
            }}
          >
            {/* Logo - Left */}
            <Box sx={{ flexShrink: 0 }}>
              <img
                src={Logo}
                style={{
                  height: "85px",
                  objectFit: "contain",
                  display: "block",
                }}
                alt="ERP Apps Logo"
              />
            </Box>

            {/* Company Details - Center */}
            <Box
              sx={{
                maxWidth: "380px", // controls how the address wraps
                textAlign: "center",
                px: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  fontSize: "0.95rem",
                  mb: 0.5,
                  color: "#000",
                  lineHeight: 1.3,
                }}
              >
                TRAILBLAZING ERP APPS SOLUTIONS LLP
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: 400,
                  color: "#555",
                  lineHeight: 1.4,
                }}
              >
                B4 G7 Mahalakshmi Appt City Link Road ADAMBAKKAM - 600 088 India
              </Typography>
            </Box>

            {/* Pay Period - Right */}
            <Box sx={{ textAlign: "right", flexShrink: 0 }}>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: "bold",
                  mb: 0.5,
                  fontSize: "0.9rem",
                  color: "#000",
                }}
              >
                Payslip For the Month
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  color: "#2196F3",
                  fontSize: "1rem",
                }}
              >
                {payslipData.payPeriod}
              </Typography>
            </Box>
          </Box>

          {/* Employee Summary Section */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: "bold",
                color: "black",
                fontSize: { xs: "0.9rem", sm: "1rem" },
              }}
            >
              EMPLOYEE SUMMARY
            </Typography>
            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
              {/* Left side - Employee details */}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: "600",
                      color: "grey.600",
                      minWidth: "145px",
                    }}
                  >
                    Employee Name :
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {payslipData.employeeName}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: "600",
                      color: "grey.600",
                      minWidth: "145px",
                    }}
                  >
                    Employee ID :
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {payslipData.employeeId}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: "600",
                      color: "grey.600",
                      minWidth: "145px",
                    }}
                  >
                    Designation :
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {payslipData.designation || "-"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: "600",
                      color: "grey.600",
                      minWidth: "145px",
                    }}
                  >
                    Date of Joining :
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {payslipData.dateOfJoining || "-"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: "600",
                      color: "grey.600",
                      minWidth: "145px",
                    }}
                  >
                    Pay Period :
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {payslipData.payPeriod}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: "600",
                      color: "grey.600",
                      minWidth: "145px",
                    }}
                  >
                    Pay Date :
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {payslipData.payDate}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: "600",
                      color: "grey.600",
                      minWidth: "145px",
                    }}
                  >
                    Bank Account No :
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {payslipData.bankAccountNo || "-"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: "600",
                      color: "grey.600",
                      minWidth: "145px",
                    }}
                  >
                    UAN :
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {payslipData.uan || "-"}
                  </Typography>
                </Box>
              </Box>

              {/* Right side - Net Pay Box */}
              <Box sx={{ width: "280px", flexShrink: 0 }}>
                <Paper
                  variant="outlined"
                  sx={{
                    border: "2px solid #9e9e9e",
                    borderRadius: 2,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      p: 2,
                      textAlign: "left",
                      borderBottom: "1px solid #9e9e9e",
                      background: "hsl(19deg 98% 81% / 50%)",
                    }}
                  >
                    <Box
                      sx={{
                        borderLeft: "3px solid #fe8348",
                        pl: 2,
                      }}
                    >
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: "bold",
                          color: "#007bdb",
                          mb: 0.5,
                          fontSize: "1.8rem",
                        }}
                      >
                        {payslipData.totalNetPay}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: "600" }}>
                        Total Net Pay
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ p: 2 }}>
                    <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: "bold", minWidth: "90px" }}
                      >
                        Paid Days :
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: "600" }}>
                        {payslipData.paidDays}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: "bold",
                          minWidth: "90px",
                          color: "red",
                        }}
                      >
                        LOP Days :
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "red", fontWeight: "600" }}
                      >
                        {payslipData.lopDays}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Box>
            </Box>
          </Box>

          {/* Earnings and Deductions */}
          <Box sx={{ width: "100%" }}>
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                border: "1px solid #9e9e9e",
                borderRadius: 2,
                overflow: "hidden",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    width: { xs: "100%", md: "50%" },
                    borderRight: { xs: "none", md: "1px solid #9e9e9e" },
                  }}
                >
                  <Table sx={{ width: "100%" }}>
                    <TableBody>
                      <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            borderBottom: "1px solid #9e9e9e",
                            borderTop: "none",
                            borderLeft: "none",
                            borderRight: "none",
                            width: "60%",
                          }}
                        >
                          EARNINGS
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: "bold",
                            borderBottom: "1px solid #9e9e9e",
                            borderTop: "none",
                            borderLeft: "none",
                            borderRight: "none",
                            width: "40%",
                          }}
                        >
                          AMOUNT
                        </TableCell>
                      </TableRow>
                      {payslipData.earnings.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell
                            sx={{
                              borderBottom:
                                index === payslipData.earnings.length - 1
                                  ? "1px solid #9e9e9e"
                                  : "1px solid #e0e0e0",
                              borderTop: "none",
                              borderLeft: "none",
                              borderRight: "none",
                              color: "grey",
                              fontWeight: "600",
                            }}
                          >
                            {item.name}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              borderBottom:
                                index === payslipData.earnings.length - 1
                                  ? "1px solid #9e9e9e"
                                  : "1px solid #e0e0e0",
                              borderTop: "none",
                              borderLeft: "none",
                              borderRight: "none",
                              fontWeight: "600",
                            }}
                          >
                            {item.amount}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            borderBottom: "none",
                            borderTop: "none",
                            borderLeft: "none",
                            borderRight: "none",
                          }}
                        >
                          Gross Earnings
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: "bold",
                            borderBottom: "none",
                            borderTop: "none",
                            borderLeft: "none",
                            borderRight: "none",
                          }}
                        >
                          {payslipData.grossEarnings}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>
                <Box sx={{ width: { xs: "100%", md: "50%" } }}>
                  <Table sx={{ width: "100%" }}>
                    <TableBody>
                      <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            borderBottom: "1px solid #9e9e9e",
                            borderTop: "none",
                            borderLeft: "none",
                            borderRight: "none",
                            width: "60%",
                          }}
                        >
                          DEDUCTIONS
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: "bold",
                            borderBottom: "1px solid #9e9e9e",
                            borderTop: "none",
                            borderLeft: "none",
                            borderRight: "none",
                            width: "40%",
                          }}
                        >
                          AMOUNT
                        </TableCell>
                      </TableRow>
                      {payslipData.deductions.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell
                            sx={{
                              borderBottom:
                                index === payslipData.deductions.length - 1
                                  ? "1px solid #9e9e9e"
                                  : "1px solid #e0e0e0",
                              borderTop: "none",
                              borderLeft: "none",
                              borderRight: "none",
                              color: "grey",
                              fontWeight: "600",
                            }}
                          >
                            {item.name}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              borderBottom:
                                index === payslipData.deductions.length - 1
                                  ? "1px solid #9e9e9e"
                                  : "1px solid #e0e0e0",
                              borderTop: "none",
                              borderLeft: "none",
                              borderRight: "none",
                              fontWeight: "600",
                            }}
                          >
                            {item.amount}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            borderBottom: "none",
                            borderTop: "none",
                            borderLeft: "none",
                            borderRight: "none",
                          }}
                        >
                          Total Deductions
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: "bold",
                            borderBottom: "none",
                            borderTop: "none",
                            borderLeft: "none",
                            borderRight: "none",
                          }}
                        >
                          {payslipData.totalDeductions}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            </TableContainer>
          </Box>

          {/* Total Net Payable */}
          <Box
            sx={{
              mt: 3,
              p: 3,
              bgcolor: "#f5f5f5",
              borderRadius: 2,
              border: "1px solid #e0e0e0",
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{ mb: 2, fontWeight: "bold", fontSize: "0.95rem" }}
            >
              TOTAL NET PAYABLE
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: "500", fontSize: "0.875rem" }}
              >
                Gross Earnings - Total Deductions
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  fontSize: "1rem",
                }}
              >
                {payslipData.netPayable}
              </Typography>
            </Box>
          </Box>

          {/* Amount in Words - Dynamically calculated */}
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <Typography
              variant="body2"
              sx={{
                fontStyle: "italic",
                color: "#555",
                fontSize: "0.85rem",
              }}
            >
              <span style={{ fontWeight: "500" }}>Amount In Words:</span>
              <span style={{ fontWeight: "600", marginLeft: "4px" }}>
                {amountInWords}
              </span>
            </Typography>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              textAlign: "center",
              mt: 4,
              pt: 2,
              borderTop: "1px dashed #ccc",
            }}
          >
            <Typography variant="caption" sx={{ color: "grey.600" }}>
              -- This is a system-generated document. --
            </Typography>
          </Box>
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default PayslipComponent;
